import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { AlertTriangle, DatabaseBackup, Loader2, ShieldCheck } from "lucide-react";
import { fmtDateTime } from "@/lib/date";

const INTENT = { headers: { "X-Launch-Reset-Intent": "confirm" } };
const LABELS = { pbos: "PBO records (incl. appointments, stage history, checklists)", activity_entries: "Conversation activity / counters",
  conference_attendance: "Conference attendance & ticket tracking", bootcamp_progress: "Bootcamp progress, dates & held backups", milestone_records: "Milestone records & history" };

function Counts({ rows, testid }) {
  return (
    <ul className="divide-y divide-slate-100 text-sm" data-testid={testid}>
      {rows.map(([k, label, n]) => (
        <li key={k} className="flex items-start justify-between gap-3 py-2" data-testid={`${testid}-${k}`}>
          <span className="text-slate-700">{label}</span><span className="font-semibold text-[#0A1F44] tabular-nums">{n}</span>
        </li>
      ))}
    </ul>
  );
}

function ConfirmBox({ phrase, label, onRun, busy, testid, danger }) {
  const [v, setV] = useState("");
  return (
    <div className="space-y-2" data-testid={testid}>
      <p className="text-sm text-slate-600">Type <code className="px-1 rounded bg-slate-100 text-[#0A1F44]" data-testid={`${testid}-phrase`}>{phrase}</code> to confirm.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input value={v} onChange={(e) => setV(e.target.value)} placeholder={phrase} data-testid={`${testid}-input`} className="h-11" />
        <Button disabled={v !== phrase || busy} onClick={() => onRun(v)} data-testid={`${testid}-btn`}
                className={danger ? "bg-[#8A1F1F] hover:bg-[#8A1F1F]/90 text-white h-11" : "h-11"}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : label}
        </Button>
      </div>
    </div>
  );
}

export default function LaunchReset() {
  const { isViewAs } = useAuth();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/launch-reset").then((r) => setD(r.data)).catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load"));
  useEffect(() => { load(); }, []);
  const run = async (path, body) => {
    setBusy(true);
    try { await api.post(`/admin/launch-reset/${path}`, body, INTENT); toast.success("Done"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); load(); }
  };
  if (isViewAs) return <p className="py-10 text-center text-sm text-slate-600" data-testid="lr-viewas">Exit View As to use this tool.</p>;
  if (!d) return <div className="py-10 text-center text-sm text-slate-400">Loading…</div>;
  const { environment: env, state } = d;
  return (
    <div className="space-y-5 max-w-3xl" data-testid="launch-reset-page">
      <Link to="/admin" className="text-xs text-slate-500 hover:text-[#0A1F44]">← Admin</Link>
      <div>
        <h1 className="font-serif text-3xl text-[#0A1F44]">Clean-launch activity reset</h1>
        <p className="text-sm text-slate-600 mt-1">One-time, owner-authorized. Deletes test activity only; accounts and settings are kept.</p>
      </div>
      <Card className={`p-4 border-2 ${env.label === "LIVE" ? "border-[#8A1F1F]" : "border-[#C9A961]"}`} data-testid="lr-env">
        <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-slate-500">Environment</div>
        <div className="font-serif text-2xl text-[#0A1F44]" data-testid="lr-env-label">{env.label}</div>
        <div className="text-sm text-slate-600 break-all" data-testid="lr-env-db">Database: {env.database}</div>
        <div className="text-sm mt-2" data-testid="lr-status">Status: <span className="font-semibold">{d.status}</span>
          {state.completed_at && <> · completed {fmtDateTime(state.completed_at)}</>}</div>
      </Card>
      <Card className="p-4 space-y-2">
        <h2 className="font-serif text-lg text-[#0A1F44] flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#8A1F1F]" /> Will be deleted (dry run — current counts)</h2>
        <Counts testid="lr-targets" rows={Object.entries(d.targets).map(([k, n]) => [k, LABELS[k], n])} />
      </Card>
      <Card className="p-4 space-y-2">
        <h2 className="font-serif text-lg text-[#0A1F44] flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-700" /> Always preserved</h2>
        <Counts testid="lr-preserved" rows={Object.entries(d.preserved).map(([k, p]) => [k, p.what, p.count])} />
        <p className="text-xs text-slate-500">No calendar events, messages, refunds or external records are touched.</p>
      </Card>
      <Card className="p-4 space-y-3" data-testid="lr-step-backup">
        <h2 className="font-serif text-lg text-[#0A1F44] flex items-center gap-2"><DatabaseBackup className="w-4 h-4" /> Step 1 — Verified backup</h2>
        {state.backup_id ? <p className="text-sm text-slate-700" data-testid="lr-backup-info">Backup <code>{state.backup_id}</code> · {fmtDateTime(state.backup_at)} · verified</p>
          : <p className="text-sm text-slate-600">No backup yet. The reset stays locked until one is created and verified.</p>}
        <Button disabled={!d.can_backup || busy} onClick={() => run("backup")} variant="outline" data-testid="lr-backup-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : state.backup_id ? "Create a fresh backup" : "Create backup"}
        </Button>
      </Card>
      <Card className="p-4 space-y-3" data-testid="lr-step-execute">
        <h2 className="font-serif text-lg text-[#0A1F44]">Step 2 — Run the reset</h2>
        {d.can_execute ? <ConfirmBox testid="lr-execute" phrase={d.phrases.reset} label="Run reset" danger busy={busy} onRun={(v) => run("execute", { confirm: v, backup_id: state.backup_id })} />
          : <p className="text-sm text-slate-600" data-testid="lr-execute-locked">{d.status === "completed" ? "Reset completed — this tool is now permanently disabled." : "Locked — create a verified backup first."}</p>}
      </Card>
      {d.can_restore && (
        <Card className="p-4 space-y-3" data-testid="lr-step-restore">
          <h2 className="font-serif text-lg text-[#0A1F44]">Recovery — restore from backup {state.backup_id}</h2>
          <ConfirmBox testid="lr-restore" phrase={d.phrases.restore} label="Restore" busy={busy} onRun={(v) => run("restore", { confirm: v, backup_id: state.backup_id })} />
        </Card>
      )}
      {d.audit.length > 0 && (
        <Card className="p-4">
          <h2 className="font-serif text-lg text-[#0A1F44] mb-2">Audit</h2>
          <ul className="text-xs text-slate-600 space-y-1" data-testid="lr-audit">
            {d.audit.map((a) => <li key={a.id}>{fmtDateTime(a.at)} · {a.event} · {a.admin_email} · {a.label}/{a.database} · {a.backup_id}</li>)}
          </ul>
        </Card>
      )}
    </div>
  );
}
